import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import { animeList } from '@/mocks/anime';
import AnimeCard from '@/components/base/AnimeCard';
import EmptyState from '@/components/base/EmptyState';
import Pagination from '@/components/base/Pagination';
import PageHero from '@/components/base/PageHero';
import FilterPanel, { type FilterState } from './components/FilterPanel';

type SortKey = 'updated' | 'views' | 'rating' | 'year';

const sortLabels: { key: SortKey; label: string }[] = [
  { key: 'updated', label: '按更新时间' },
  { key: 'views', label: '按热度' },
  { key: 'rating', label: '按评分' },
  { key: 'year', label: '按年份' },
];

export default function Category() {
  const [searchParams] = useSearchParams();

  const initialType = searchParams.get('type') || '';
  const initialRegion = searchParams.get('region') || '';

  const [filters, setFilters] = useState<FilterState>({
    type: initialType,
    region: initialRegion,
    year: '',
    rating: '',
  });
  const [sort, setSort] = useState<SortKey>('updated');
  const [pageSize, setPageSize] = useState(12);
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilters((f) => ({ ...f, type: initialType, region: initialRegion }));
  }, [initialType, initialRegion]);

  const heading = initialType || initialRegion || '全部动漫';
  useSeo(
    `${heading}动画作品 - 妖精动漫`,
    `妖精动漫${heading}频道，收录丰富优质作品，支持按类型、地区、年份与评分筛选，快速找到你感兴趣的动漫。`,
    `${heading},动漫分类,动漫筛选,妖精动漫`,
  );

  const filtered = useMemo(() => {
    let list = animeList.filter((a) => {
      if (filters.type && a.type !== filters.type) return false;
      if (filters.region && a.region !== filters.region) return false;
      if (filters.year && a.year !== Number(filters.year)) return false;
      if (filters.rating && a.rating < Number(filters.rating)) return false;
      return true;
    });

    list = [...list].sort((a, b) => {
      switch (sort) {
        case 'views':
          return b.views - a.views;
        case 'rating':
          return b.rating - a.rating;
        case 'year':
          return b.year - a.year;
        default:
          return a.updatedAt < b.updatedAt ? 1 : -1;
      }
    });

    return list;
  }, [filters, sort]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleFilterChange = (patch: Partial<FilterState>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };
  const handleReset = () => {
    setFilters({ type: '', region: '', year: '', rating: '' });
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHero
        title={heading}
        subtitle="从类型、地区、年份到评分，多维筛选帮你找到想看的那一部。"
        crumbs={[{ label: '首页', to: '/' }, { label: '动漫分类' }, ...(heading !== '全部动漫' ? [{ label: heading }] : [])]}
      />

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
          <div className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-20">
              <FilterPanel filters={filters} onChange={handleFilterChange} onReset={handleReset} resultCount={filtered.length} />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-5 pb-4 border-b border-background-200">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-filter-3-line"></i>筛选
              </button>
              <span className="text-sm text-foreground-500 whitespace-nowrap">
                共 <span className="text-accent-600 font-semibold">{filtered.length}</span> 部作品
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <div className="flex items-center gap-1 bg-background-100 rounded-md p-1">
                  {sortLabels.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => { setSort(s.key); setPage(1); }}
                      className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${sort === s.key ? 'bg-background-50 text-primary-600 font-medium' : 'text-foreground-500 hover:text-foreground-800'}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="h-9 px-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
                >
                  <option value={12}>显示 12</option>
                  <option value={20}>显示 20</option>
                  <option value={30}>显示 30</option>
                </select>
              </div>
            </div>

            {paged.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                  {paged.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            ) : (
              <EmptyState
                icon="ri-search-eye-line"
                title="没有找到符合条件的作品"
                description="试试减少筛选条件，或者换个类型、地区重新看看。"
                actionText="重置筛选"
                actionTo="/category"
              />
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setDrawerOpen(false)}></div>
          <div className="relative ml-auto w-[85%] max-w-sm h-full bg-background-50 overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between px-4 py-4 border-b border-background-200 sticky top-0 bg-background-50">
              <span className="text-base font-bold text-foreground-950">筛选条件</span>
              <button type="button" onClick={() => setDrawerOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer" aria-label="关闭筛选">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="p-4">
              <FilterPanel filters={filters} onChange={handleFilterChange} onReset={handleReset} resultCount={filtered.length} />
            </div>
            <div className="sticky bottom-0 p-4 bg-background-50 border-t border-background-200">
              <button type="button" onClick={() => setDrawerOpen(false)} className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer">
                查看 {filtered.length} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}